import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, BarChart3, Megaphone, Lock, Settings2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { useConsent } from '../context/ConsentContext';
import { useAuth } from '../context/AuthContext';

/**
 * DSGVO-konformer Cookie-Banner mit 3 Modi:
 *   1. Initial-View ("Alle akzeptieren" / "Nur notwendige" / "Einstellungen")
 *   2. Settings-View (granulare Toggles für Statistik & Marketing)
 *
 * Tracker werden über den ConsentContext geladen. Über den Footer-Link
 * "Cookie-Einstellungen" kann der Banner jederzeit wieder geöffnet werden.
 */

const CategoryRow = ({ icon: Icon, title, description, enabled, onChange, locked, lockReason = 'Erforderlich', testid }) => (
  <div
    className={`border rounded-xl p-4 transition-colors ${
      enabled ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-gray-700 bg-gray-900/40'
    }`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <div className={`p-2 rounded-lg ${enabled ? 'bg-yellow-500/15 text-yellow-400' : 'bg-gray-800 text-gray-400'}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-medium flex items-center gap-2 flex-wrap">
            {title}
            {locked && <span className="text-[10px] bg-gray-700 text-gray-300 px-2 py-0.5 rounded">{lockReason}</span>}
          </p>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => !locked && onChange(!enabled)}
        disabled={locked}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          enabled ? 'bg-yellow-500' : 'bg-gray-700'
        } ${locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid={`${testid}-toggle`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
            enabled ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  </div>
);

const CookieBanner = () => {
  const { bannerOpen, categories, config, acceptAll, rejectAll, saveConsent, openBanner } = useConsent();
  const location = useLocation();
  const { company } = useAuth();
  const [mode, setMode] = useState('initial'); // 'initial' | 'settings'
  const [draft, setDraft] = useState({ statistik: false, marketing: false });

  // Beim Öffnen den letzten gespeicherten Status als Draft übernehmen
  useEffect(() => {
    if (bannerOpen) {
      setDraft({ statistik: !!categories.statistik, marketing: !!categories.marketing });
      setMode('initial');
    }
  }, [bannerOpen, categories.statistik, categories.marketing]);

  // Globalen Trigger registrieren, damit Footer-Link den Banner öffnen kann
  useEffect(() => {
    window.openCookieSettings = openBanner;
    return () => { delete window.openCookieSettings; };
  }, [openBanner]);

  // Auf Admin-/Mitarbeiter-Cockpit-Routen Banner unterdrücken:
  // - eingeloggter Admin braucht den Banner nicht (kennt die Implikationen)
  // - Speichern einer Tracking-Änderung im /admin würde sonst sofort den Banner über die UI legen
  const isCockpitRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/chef-cockpit') || location.pathname.startsWith('/mitarbeiter');
  if (!bannerOpen) return null;
  if (isCockpitRoute && (company?.isAdmin || company?.role === 'admin')) return null;

  // Aktive Tracker für die Beschreibung in "Statistik" / "Marketing"
  const marketingTools = [
    config?.googleAds?.enabled && 'Google Ads',
    config?.metaPixel?.enabled && 'Meta-Pixel',
    config?.linkedInInsight?.enabled && 'LinkedIn Insight',
    config?.tiktokPixel?.enabled && 'TikTok-Pixel',
  ].filter(Boolean);
  const statistikTools = [
    config?.googleAnalytics?.enabled && 'Google Analytics 4',
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end md:items-center justify-center p-3 md:p-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
      data-testid="cookie-banner"
    >
      <div
        className="w-full max-w-2xl bg-gradient-to-br from-gray-950 to-black border border-yellow-500/30 rounded-2xl shadow-2xl shadow-yellow-500/10 overflow-hidden"
        data-testid="cookie-modal"
      >
        <div className="p-5 md:p-7">
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 rounded-xl bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
              <Shield className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 id="cookie-title" className="text-white text-lg md:text-xl font-bold">
                Wir respektieren deine Privatsphäre
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Smartvolt Academy nutzt Cookies und ähnliche Technologien, um die Plattform bereitzustellen,
                ihre Nutzung zu analysieren und – wenn du zustimmst – relevantere Inhalte zu zeigen.
                Du kannst deine Einwilligung jederzeit über den Link „Cookie-Einstellungen“ am Seitenende widerrufen.
              </p>
            </div>
          </div>

          {mode === 'initial' && (
            <>
              <div className="mt-4 grid gap-2 text-xs text-gray-400">
                <p className="leading-relaxed">
                  Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO &amp; § 25 Abs. 1 TTDSG.
                  Eingesetzte Tools: <span className="text-gray-200">Digistore24</span> (Zahlung – erforderlich)
                  {statistikTools.length > 0 && <>, <span className="text-gray-200">{statistikTools.join(', ')}</span> (Statistik)</>}
                  {marketingTools.length > 0 && <>, <span className="text-gray-200">{marketingTools.join(', ')}</span> (Marketing)</>}
                  .
                </p>
                <p className="text-[11px] text-gray-500">
                  Details in unserer <Link to="/legal/datenschutz" className="text-yellow-500 hover:underline">Datenschutzerklärung</Link>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-5">
                <Button
                  variant="outline"
                  onClick={rejectAll}
                  className="flex-1 border-gray-700 text-gray-200 hover:bg-gray-800"
                  data-testid="cookie-reject-btn"
                >
                  <Lock className="h-4 w-4 mr-2" /> Nur notwendige
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setMode('settings')}
                  className="flex-1 border-gray-700 text-gray-200 hover:bg-gray-800"
                  data-testid="cookie-settings-btn"
                >
                  <Settings2 className="h-4 w-4 mr-2" /> Individuelle Einstellungen
                </Button>
                <Button
                  onClick={acceptAll}
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                  data-testid="cookie-accept-btn"
                >
                  <Check className="h-4 w-4 mr-2" /> Alle akzeptieren
                </Button>
              </div>
            </>
          )}

          {mode === 'settings' && (
            <>
              <div className="space-y-3 mt-5" data-testid="cookie-settings-panel">
                <CategoryRow
                  icon={Lock}
                  title="Notwendig"
                  description="Login, Warenkorb/Buchung und Zahlungsabwicklung über Digistore24. Diese Cookies sind technisch erforderlich und können nicht deaktiviert werden."
                  enabled={true}
                  onChange={() => {}}
                  locked
                  testid="cookie-cat-essential"
                />
                <CategoryRow
                  icon={BarChart3}
                  title="Statistik"
                  description={
                    statistikTools.length > 0
                      ? `Anonyme Reichweitenmessung mit ${statistikTools.join(', ')}, um Inhalte zu verbessern.`
                      : 'Aktuell sind keine Statistik-Dienste aktiviert.'
                  }
                  enabled={!!draft.statistik}
                  onChange={(v) => setDraft((d) => ({ ...d, statistik: v }))}
                  locked={statistikTools.length === 0}
                  lockReason="Nicht aktiviert"
                  testid="cookie-cat-statistik"
                />
                <CategoryRow
                  icon={Megaphone}
                  title="Marketing"
                  description={
                    marketingTools.length > 0
                      ? `Personalisierte Anzeigen & Conversion-Tracking via ${marketingTools.join(', ')}.`
                      : 'Aktuell sind keine Marketing-Dienste aktiviert.'
                  }
                  enabled={!!draft.marketing}
                  onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
                  locked={marketingTools.length === 0}
                  lockReason="Nicht aktiviert"
                  testid="cookie-cat-marketing"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-5">
                <Button
                  variant="outline"
                  onClick={() => setMode('initial')}
                  className="flex-1 border-gray-700 text-gray-200 hover:bg-gray-800"
                  data-testid="cookie-back-btn"
                >
                  <X className="h-4 w-4 mr-2" /> Zurück
                </Button>
                <Button
                  onClick={() => saveConsent(draft)}
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                  data-testid="cookie-save-btn"
                >
                  <Check className="h-4 w-4 mr-2" /> Auswahl speichern
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
